import React from 'react';

export const EMOJI_CLUSTER_RE = /\p{Extended_Pictographic}(?:\uFE0E|\uFE0F)?(?:\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0E|\uFE0F)?(?:\p{Emoji_Modifier})?)*/gu;

const PUBLIC_BASE = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const EMOJI_DATA_URL = `${PUBLIC_BASE}/emoji/emoji.json`;
const EMOJI_SHEET_URL = `${PUBLIC_BASE}/emoji/sheet_google_32_clean.png`;

let emojiSpriteIndexCache = null;
let emojiSpriteIndexPromise = null;

const normalizeUnified = (value) => String(value || '').trim().toUpperCase();

const unifiedToUnicode = (code) => {
  const normalized = normalizeUnified(code);
  if (!normalized) return '';
  try {
    return normalized
      .split('-')
      .filter(Boolean)
      .map((hex) => String.fromCodePoint(parseInt(hex, 16)))
      .join('');
  } catch {
    return '';
  }
};

const toSpriteStyle = (sheetX, sheetY, cols, rows) => {
  const safeCols = Math.max(1, cols);
  const safeRows = Math.max(1, rows);
  return {
    backgroundImage: `url("${EMOJI_SHEET_URL}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `calc(var(--emoji-size) * ${safeCols}) calc(var(--emoji-size) * ${safeRows})`,
    backgroundPosition: `calc(var(--emoji-size) * -${sheetX}) calc(var(--emoji-size) * -${sheetY})`,
    backgroundColor: 'transparent',
  };
};

const buildEmojiSpriteIndex = (emojiData) => {
  const map = new Map();
  if (!Array.isArray(emojiData)) return map;

  let cols = 0;
  let rows = 0;

  const collectBounds = (entry) => {
    const x = Number(entry?.sheet_x);
    const y = Number(entry?.sheet_y);
    if (Number.isFinite(x)) cols = Math.max(cols, x + 1);
    if (Number.isFinite(y)) rows = Math.max(rows, y + 1);
  };

  for (const emoji of emojiData) {
    collectBounds(emoji);
    const skinVariations = emoji?.skin_variations || {};
    for (const variation of Object.values(skinVariations)) collectBounds(variation);
  }

  const registerEmoji = (unified, sheetX, sheetY) => {
    if (!Number.isFinite(sheetX) || !Number.isFinite(sheetY)) return;
    const unicode = unifiedToUnicode(unified);
    if (!unicode || map.has(unicode)) return;
    map.set(unicode, toSpriteStyle(sheetX, sheetY, cols, rows));
  };

  for (const emoji of emojiData) {
    const baseX = Number(emoji?.sheet_x);
    const baseY = Number(emoji?.sheet_y);
    registerEmoji(emoji?.unified, baseX, baseY);
    registerEmoji(emoji?.non_qualified, baseX, baseY);
    const skinVariations = emoji?.skin_variations || {};
    for (const variation of Object.values(skinVariations)) {
      const varX = Number(variation?.sheet_x);
      const varY = Number(variation?.sheet_y);
      registerEmoji(variation?.unified, varX, varY);
      registerEmoji(variation?.non_qualified, varX, varY);
    }
  }

  return map;
};

const loadEmojiSpriteIndex = async () => {
  if (emojiSpriteIndexCache) return emojiSpriteIndexCache;
  if (emojiSpriteIndexPromise) return emojiSpriteIndexPromise;

  emojiSpriteIndexPromise = fetch(EMOJI_DATA_URL)
    .then((res) => {
      if (!res.ok) throw new Error(`emoji index load failed: ${res.status}`);
      return res.json();
    })
    .then((json) => {
      emojiSpriteIndexCache = buildEmojiSpriteIndex(json);
      return emojiSpriteIndexCache;
    })
    .catch(() => new Map());

  return emojiSpriteIndexPromise;
};

export function useEmojiSpriteIndex() {
  const [index, setIndex] = React.useState(() => emojiSpriteIndexCache || new Map());

  React.useEffect(() => {
    let cancelled = false;
    loadEmojiSpriteIndex().then((next) => {
      if (!cancelled) setIndex(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return index;
}

export function resolveEmojiSpriteStyle(index, emoji) {
  if (!emoji || !index?.size) return null;
  const direct = index.get(emoji);
  if (direct) return direct;

  const noVariant = emoji.replace(/\uFE0F|\uFE0E/g, '');
  const noVariantHit = index.get(noVariant);
  if (noVariantHit) return noVariantHit;

  const unified = normalizeUnified(Array.from(emoji).map((char) => char.codePointAt(0).toString(16)).join('-'));
  const strippedUnified = unified.replace(/-FE0F/g, '').replace(/-FE0E/g, '');
  return index.get(unifiedToUnicode(unified)) || index.get(unifiedToUnicode(strippedUnified)) || null;
}
